import { useEffect, useState } from "react";

/**
 * Breakpoints padronizados do app (px). Alinhados ao que os componentes do
 * design-system já usavam (768 / 480), com 1024 como fronteira do desktop.
 * Não são tokens de CSS (media queries não leem custom properties); servem para
 * a lógica em JS e como referência única dos valores usados nos `@media`.
 */
export const BREAKPOINTS = { sm: 480, md: 768, lg: 1024 } as const;

/**
 * Retorna `true` enquanto a media query casar. Reage a resize/rotação via
 * `matchMedia`. SSR-safe: retorna `false` quando não há `window`.
 */
export default function useMediaQuery(query: string): boolean {
  const getMatches = (): boolean =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatches(mql.matches);
    onChange(); // sincroniza caso a query tenha mudado entre o render e o efeito
    // addEventListener é o moderno; addListener é fallback p/ Safari antigo.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/**
 * Largura máxima (px) em que o SHELL usa a versão mobile: menu lateral vira drawer
 * sobreposto e a topbar vira navbar de app. Acima disso, layout desktop.
 * Maior que `lg` de propósito — entre ~1024 e 1200 o shell desktop não tem espaço
 * suficiente (breadcrumb quebrava, conteúdo espremido), então já entra em modo mobile.
 */
export const SHELL_MOBILE_MAX = 1200;

/** Abaixo de `SHELL_MOBILE_MAX` tratamos como mobile/tablet → o menu vira drawer. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${SHELL_MOBILE_MAX - 0.02}px)`);
}
